<template>
  <main-layout>
    <div>
      <v-tabs v-model="selectedTab" slider-color="primary" touchless>
        <v-tab v-for="(item, index) in tabs" :key="`tab_${index}`" ripple>{{ item }}</v-tab>
      </v-tabs>
      <v-container fluid v-if="[0, 1, 2].includes(selectedTab)">
        <v-layout wrap>
          <v-flex xs12 sm8>
            <v-btn
              v-for="(item, index) in channel"
              :key="`channel_${index}`"
              depressed
              :outline="channelFilter != item.value"
              :color="channelFilter == item.value ? 'primary' : 'grey'"
              @click="handleChangeChannel(item.value)"
              class="ma-0 mr-1 mb-1"
            >
              <em v-if="item.icon" :class="`mr-1 ${item.icon}`" />
              {{ item.label }}
            </v-btn>
          </v-flex>
          <v-flex xs12 sm4 class="text-sm-right">
            <template v-if="isLoading">
              <v-btn :loading="true" color="grey" outline class="ma-0 mb-1"></v-btn>
            </template>
            <template v-else>
              <v-btn
                v-if="$store.getters.getPackage != 'premium'"
                outline
                v-tooltip.top="'Unlock to PREMIUM plan'"
                color="grey"
                class="ma-0 mb-1"
              >
                <i class="fas fa-file-export mr-1"></i>
                {{ $t('export') }}
                <v-premium-lock></v-premium-lock>
              </v-btn>
              <v-btn
                v-else-if="$store.getters.getPackage == 'premium'"
                @click="handleExportCSV"
                color="grey"
                outline
                class="ma-0 mb-1"
              >
                <i class="fas fa-file-export mr-1"></i>
                {{ $t('export') }}
              </v-btn>
            </template>
          </v-flex>
          <DateFilter v-model="selectedDate" :defaultFilter="'today'" :key="timestamp"></DateFilter>
        </v-layout>
      </v-container>
      <GptDashboard
        v-if="[0].includes(selectedTab)"
        :selectedDate="selectedDate"
        :channelFilter="channelFilter"
        @loading="handleLoading"
      />
      <GptQuestion
        v-if="[1].includes(selectedTab)"
        :selectedDate="selectedDate"
        :channelFilter="channelFilter"
        @loading="handleLoading"
      />
      <GptConversation
        v-if="[2].includes(selectedTab)"
        :selectedDate="selectedDate"
        :channelFilter="channelFilter"
        @loading="handleLoading"
      />
      <GptQuota v-if="[3].includes(selectedTab)" />
    </div>
  </main-layout>
</template>

<script>
import gptApi from '@/api/gpt'
import GptConversation from '@/components/view/chatgpt/GptConversation'
import GptDashboard from '@/components/view/chatgpt/GptDashboard'
import GptQuestion from '@/components/view/chatgpt/GptQuestion'
import GptQuota from '@/components/view/chatgpt/GptQuota'
import DateFilter from '@/components/view/dashboard/DateFilter.vue'
import dayjs from 'dayjs'
import escape from 'lodash.escape'
import { mapGetters } from 'vuex'

export default {
  data() {
    return {
      tabs: [
        this.$t('Dashboard'),
        this.$t('Default Conversation'),
        this.$t('ChatGPT Conversation'),
        this.$t('Quota GPT')
      ],
      channel: [
        { label: this.$t('All'), value: 'all', icon: '' },
        { label: this.$t('Facebook'), value: 'facebook', icon: 'fab fa-facebook-square' },
        { label: this.$t('Line'), value: 'line', icon: 'fab fa-line' },
        { label: this.$t('Web Chat'), value: 'webchat', icon: 'fas fa-globe' }
      ],
      selectedTab: 0,
      selectedDate: {
        start: dayjs().format('YYYY-MM-DD'),
        end: dayjs().format('YYYY-MM-DD')
      },
      channelFilter: 'all',
      timestamp: 0,
      isLoading: false,
      chunkLimit: 10000
    }
  },
  components: {
    DateFilter,
    GptDashboard,
    GptQuestion,
    GptConversation,
    GptQuota
  },
  watch: {
    selectedTab() {
      this.handleResetState()
    }
  },
  computed: {
    ...mapGetters({
      selectedBot: 'selectedBot'
    })
  },
  methods: {
    handleChangeChannel(val) {
      this.channelFilter = val
    },
    handleResetState() {
      this.channelFilter = 'all'
      this.selectedDate = {
        start: dayjs().format('YYYY-MM-DD'),
        end: dayjs().format('YYYY-MM-DD')
      }
      this.timestamp = dayjs().unix()
    },
    handleExportCSV() {
      switch (this.selectedTab) {
        case 0:
          this.handleExportAllConversation('all')
          break
        case 1:
          this.handleExportAllConversation(false)
          break
        case 2:
          this.handleExportGPTConversation()
          break
        default:
          this.handleExportAllConversation('all')
          break
      }
      if (this.isLoading) {
        return false
      }
    },
    handleLoading(loading) {
      this.isLoading = loading
    },
    async handleExportAllConversation(type) {
      try {
        this.isLoading = true
        const response = await gptApi.getChatGPTSummarizeUserQuestion({
          pageId: this.selectedBot.pageid,
          params: {
            limit: this.chunkLimit,
            offset: 0,
            date_from: this.selectedDate.start,
            date_to: this.selectedDate.end,
            channel: this.channelFilter,
            gpt_reply: type
          }
        })
        const { status, data } = response
        if ([200].includes(status)) {
          const chunkLength = Math.ceil(data.total / this.chunkLimit)
          if (chunkLength <= 0) {
            this.$swal.fire({
              title: this.$t('No Data'),
              type: 'warning',
              confirmButtonText: this.$t('Confirm'),
              reverseButtons: true
            })
          }
          await Promise.all(
            Array(chunkLength)
              .fill(1)
              .map(async (item, index) => {
                await this.sleep(index * 300)
                const page = item + index
                const offset = page * this.chunkLimit - this.chunkLimit
                const response = await gptApi.getChatGPTSummarizeUserQuestion({
                  pageId: this.selectedBot.pageid,
                  params: {
                    limit: this.chunkLimit,
                    offset: offset,
                    date_from: this.selectedDate.start,
                    date_to: this.selectedDate.end,
                    channel: this.channelFilter,
                    gpt_reply: type
                  }
                })
                const { data } = response
                const list = data.data
                let csvContent = ''
                csvContent += 'Summarize Question,Summarize Question Total'
                list.map(item => {
                  csvContent += '\r\n'
                  csvContent += `${item.summarize_question},${+item.summarize_question_total}`
                })
                this.exportCsv(csvContent, `chat_gpt_all_conversation_${page}`)
              })
          )
        }
      } catch (error) {
        console.error(error)
      } finally {
        this.isLoading = false
      }
    },
    async handleExportGPTConversation() {
      try {
        this.isLoading = true
        const response = await gptApi.getChatGPTConversation({
          pageId: this.selectedBot.pageid,
          params: {
            limit: this.chunkLimit,
            offset: 0,
            date_from: this.selectedDate.start,
            date_to: this.selectedDate.end,
            channel: this.channelFilter
          }
        })
        const { status, data } = response
        if ([200].includes(status)) {
          const chunkLength = Math.ceil(data.total / this.chunkLimit)
          if (chunkLength <= 0) {
            this.$swal.fire({
              title: this.$t('No Data'),
              type: 'warning',
              confirmButtonText: this.$t('Confirm'),
              reverseButtons: true
            })
          }
          await Promise.all(
            Array(chunkLength)
              .fill(1)
              .map(async (item, index) => {
                await this.sleep(index * 300)
                const page = item + index
                const offset = page * this.chunkLimit - this.chunkLimit
                const response = await gptApi.getChatGPTConversation({
                  pageId: this.selectedBot.pageid,
                  params: {
                    limit: this.chunkLimit,
                    offset: offset,
                    date_from: this.selectedDate.start,
                    date_to: this.selectedDate.end,
                    channel: this.channelFilter
                  }
                })
                const { data } = response
                const list = data.data
                let csvContent = ''
                csvContent += 'ID,Question,Summarize Question,Answer Result,Created Timestamp'
                list.map(item => {
                  const question = escape(item.question.replaceAll(',', ''))
                  csvContent += '\r\n'
                  csvContent += `${item.id},${question},${item.summarize_question.replaceAll(
                    ',',
                    ''
                  )},${item.answer_result.replaceAll(',', '')},${dayjs(
                    item.created_timestamp
                  ).format('DD/MM/YYYY HH:mm:ss')}`
                })
                this.exportCsv(csvContent, `chat_gpt_conversation_${page}`)
              })
          )
        }
      } catch (error) {
        console.error(error)
      } finally {
        this.isLoading = false
      }
    },
    sleep(ms) {
      return new Promise(resolve => setTimeout(resolve, ms))
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/view/chatgpt/GptAnalyticsView.vue